/**
 * Composants et icones partages.
 *
 * Tout tient dans un fichier : l'app compte une quinzaine de composants
 * communs, les eclater en autant de fichiers couterait plus de navigation
 * qu'il n'en ferait gagner.
 *
 * Les icones sont du SVG inline. Aucune police d'icones, aucune bibliotheque :
 * la politique de securite du site interdit les scripts tiers et l'app doit
 * fonctionner integralement hors ligne.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

/** Attributs communs a tous les traces : un seul endroit pour l'epaisseur. */
const T = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

export function IconeLune() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...T} d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
    </svg>
  )
}

export function IconeListe() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...T} d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  )
}

export function IconeStats() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...T} d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  )
}

export function IconeReglages() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle {...T} cx="12" cy="12" r="3" />
      <path
        {...T}
        d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z"
      />
    </svg>
  )
}

export function IconeChevron() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path {...T} d="m9 18 6-6-6-6" />
    </svg>
  )
}

export function IconeRetour() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path {...T} d="M19 12H5m7-7-7 7 7 7" />
    </svg>
  )
}

export function IconePlus() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...T} d="M12 5v14M5 12h14" />
    </svg>
  )
}

export function IconeCopier() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect {...T} x="9" y="9" width="12" height="12" rx="2" />
      <path {...T} d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  )
}

export function IconeCheck() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...T} d="m20 6-11 11-5-5" />
    </svg>
  )
}

export function IconeAlerte() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path {...T} d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Structure de page
// ---------------------------------------------------------------------------

export function Entete({
  titre,
  sous,
  retour,
  action,
}: {
  titre: string
  sous?: string
  retour?: boolean
  action?: ReactNode
}) {
  const navigate = useNavigate()
  return (
    <header className="entete">
      {retour && (
        <button
          type="button"
          className="btn btn--fantome btn--petit"
          onClick={() => navigate(-1)}
          aria-label="Retour"
        >
          <IconeRetour />
        </button>
      )}
      <div className="entete__titre">
        <h1>{titre}</h1>
        {sous && <span className="entete__sous">{sous}</span>}
      </div>
      {action}
    </header>
  )
}

export function Carte({
  titre,
  action,
  plate,
  children,
}: {
  titre?: string
  action?: ReactNode
  plate?: boolean
  children: ReactNode
}) {
  return (
    <section className={plate ? 'carte carte--plate' : 'carte'}>
      {(titre || action) && (
        <div className="carte__titre">
          {titre && <h2>{titre}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

export function Encart({
  ton = 'info',
  children,
}: {
  ton?: 'info' | 'alerte' | 'danger' | 'ok'
  children: ReactNode
}) {
  return <div className={`encart encart--${ton}`}>{children}</div>
}

export function Puce({
  ton = 'neutre',
  children,
}: {
  ton?: 'neutre' | 'ok' | 'alerte' | 'info'
  children: ReactNode
}) {
  return <span className={ton === 'neutre' ? 'puce' : `puce puce--${ton}`}>{children}</span>
}

/**
 * Bascule a deux etats.
 *
 * `aria-pressed` porte l'etat et le libelle reste lisible dans les deux
 * positions : la couleur ne fait que redire ce que le texte dit deja.
 */
export function Bascule<T extends string>({
  valeur,
  options,
  onChange,
  etiquette,
}: {
  valeur: T
  options: { valeur: T; libelle: string }[]
  onChange: (v: T) => void
  etiquette?: string
}) {
  return (
    <div className="bascule" role="group" aria-label={etiquette}>
      {options.map((o) => (
        <button
          key={o.valeur}
          type="button"
          aria-pressed={valeur === o.valeur}
          onClick={() => onChange(o.valeur)}
        >
          {o.libelle}
        </button>
      ))}
    </div>
  )
}

export function Champ({
  libelle,
  aide,
  children,
}: {
  libelle: string
  aide?: string
  children: ReactNode
}) {
  return (
    <label className="champ">
      <span className="champ__libelle">{libelle}</span>
      {children}
      {aide && <span className="champ__aide">{aide}</span>}
    </label>
  )
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

export function Confirmation({
  titre,
  message,
  libelleConfirmer = 'Confirmer',
  danger,
  onConfirmer,
  onAnnuler,
}: {
  titre: string
  message: string
  libelleConfirmer?: string
  danger?: boolean
  onConfirmer: () => void
  onAnnuler: () => void
}) {
  // Echap ferme la modale : sur iPhone il n'y a pas de clavier, mais l'app
  // s'ouvre aussi depuis un navigateur de bureau pendant le developpement.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAnnuler()
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [onAnnuler])

  return (
    <div className="modale-fond" role="presentation" onClick={onAnnuler}>
      <div
        className="modale"
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2>{titre}</h2>
          <p className="doux" style={{ marginTop: 6 }}>
            {message}
          </p>
        </div>
        <div className="boutons">
          <button type="button" className="btn grandir" onClick={onAnnuler}>
            Annuler
          </button>
          <button
            type="button"
            className={danger ? 'btn btn--danger grandir' : 'btn btn--principal grandir'}
            onClick={onConfirmer}
          >
            {libelleConfirmer}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Copie dans le presse-papiers
// ---------------------------------------------------------------------------

/**
 * Bouton de copie avec repli sur `execCommand`.
 *
 * `navigator.clipboard` n'existe que sur origine securisee : en developpement
 * sur l'IP du reseau local, en clair, il est absent. Or c'est precisement la
 * qu'on teste la page Installation, dont l'URL doit pouvoir etre copiee.
 */
export function BoutonCopier({ texte, libelle = 'Copier' }: { texte: string; libelle?: string }) {
  const [copie, setCopie] = useState(false)

  const copier = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(texte)
      } else {
        const champ = document.createElement('textarea')
        champ.value = texte
        champ.setAttribute('readonly', '')
        champ.style.position = 'absolute'
        champ.style.left = '-9999px'
        document.body.appendChild(champ)
        champ.select()
        document.execCommand('copy')
        document.body.removeChild(champ)
      }
      setCopie(true)
      setTimeout(() => setCopie(false), 2000)
    } catch {
      setCopie(false)
    }
  }, [texte])

  return (
    <button type="button" className="btn btn--petit" onClick={() => void copier()}>
      {copie ? <IconeCheck /> : <IconeCopier />}
      {copie ? 'Copié' : libelle}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Asynchrone
// ---------------------------------------------------------------------------

export interface EtatAsync<T> {
  valeur: T | undefined
  chargement: boolean
  recharger: () => void
}

/**
 * Pour les lectures qui ne passent pas par Dexie (quota de stockage, calculs
 * ponctuels). Les donnees reactives utilisent `useLiveQuery`.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): EtatAsync<T> {
  const [valeur, setValeur] = useState<T | undefined>(undefined)
  const [chargement, setChargement] = useState(true)
  const [tick, setTick] = useState(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    let vivant = true
    setChargement(true)
    fnRef
      .current()
      .then((v) => {
        if (vivant) setValeur(v)
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        if (vivant) setChargement(false)
      })
    return () => {
      vivant = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { valeur, chargement, recharger: () => setTick((t) => t + 1) }
}
